import { CheckCircle2, ChevronDown, XCircle } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { type AutoMergeMode, TUNABLES } from "../../shared/tunables";
import { StoreAlertPicker } from "../components/StoreAlertPicker";
import { useTunables } from "../lib/tunables";
import { Button, InfoTip, Page, StateLabel } from "../ui";
import { Blank, Choice, Group, Row, Switch } from "./settings/controls";

type StatusRes = {
	configured: boolean;
	fromEnv: boolean;
	hasDbValue: boolean;
};

type OpenRouterStatusRes = StatusRes & {
	model: string | null;
	modelFromEnv: boolean;
};

type ModelOption = {
	id: string;
	name: string | null;
	promptPerMillion: number | null;
	completionPerMillion: number | null;
};

type CustomerForm = {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	address1: string;
	address2: string;
	postal: string;
	city: string;
};

const EMPTY_CUSTOMER: CustomerForm = {
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	address1: "",
	address2: "",
	postal: "",
	city: "",
};

const SECTIONS = [
	{ id: "alerts", label: "Alerts" },
	{ id: "matching", label: "Matching" },
	{ id: "sweeps", label: "Sweeps and watches" },
	{ id: "buying", label: "Buying" },
	{ id: "housekeeping", label: "Housekeeping" },
] as const;

const MERGE_LABELS: Record<AutoMergeMode, string> = {
	off: "Off",
	title: "Identical titles",
	title_ai: "Titles and AI matches",
};

const inputClass =
	"h-8 min-w-0 rounded-md border border-hairline bg-ground px-2.5 text-[13px] pointer-coarse:h-10 text-ink placeholder:text-muted hover:border-line";

export function Settings() {
	const { get } = useTunables();

	return (
		<Page
			width="wide"
			title="Settings"
			help={
				<InfoTip label="About settings">
					Each setting saves as soon as you change it. The Discord webhook, OpenRouter key and model can also be set as
					environment variables on the server, which win over what's saved here.
				</InfoTip>
			}
		>
			<div className="lg:grid lg:grid-cols-[152px_minmax(0,1fr)] lg:gap-6">
				{/* top-16 keeps the list clear of the app's 48px sticky top bar. */}
				<nav aria-label="Settings sections" className="hidden lg:block">
					<ul className="sticky top-16 flex flex-col gap-px text-[13px]">
						{SECTIONS.map((s) => (
							<li key={s.id}>
								<a
									href={`#${s.id}`}
									onClick={(e) => {
										// The app routes on the hash, so jump without touching it.
										e.preventDefault();
										document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
									}}
									className="block rounded-md px-2 py-1 text-muted hover:bg-raised hover:text-ink"
								>
									{s.label}
								</a>
							</li>
						))}
					</ul>
				</nav>

				<div className="flex min-w-0 flex-col gap-5">
					<Group
						id="alerts"
						title="Alerts"
						intro="Alerts go to Discord: for watched products, for anything matching an enabled rule, or for everything."
					>
						<DiscordRow />
						<Row
							help="Everything any sweep or watch finds, watched or not. Useful at first, noisy with many stores."
							control={<Switch t={TUNABLES.allEvents} label="Send every event" />}
						>
							Send every event to Discord
						</Row>
						<Row
							help="A price going up is rarely worth a ping, so these are off unless you turn them on here or send every event."
							control={<Switch t={TUNABLES.priceIncreases} label="Alert on price increases" />}
						>
							Alert when a price goes up
						</Row>
						<Row help="0 sends every one. Counts only alerts that were actually delivered.">
							Don't repeat the same alert for a product within
							<Blank t={TUNABLES.repeatWindow} label="Repeat window in minutes" />
						</Row>
						<ShopAlertsRow />
					</Group>

					<Group
						id="matching"
						title="Matching"
						intro="How listings from different stores become one item. Same EAN always groups; these cover the rest."
					>
						<OpenRouterRows />
						<Row
							help="Every 10 minutes, reads listings the AI hasn't seen yet, those without an EAN first. Needs a key and a model above."
							control={<Switch t={TUNABLES.autoExtract} label="Read new listings automatically" />}
						>
							Read new listings with AI automatically
						</Row>
						<Row
							dim={!get(TUNABLES.autoExtract)}
							help="Spread evenly over the hour. Each listing costs a fraction of a cent with a cheap model."
						>
							Read at most
							<Blank t={TUNABLES.autoExtractPerHour} label="Listings per hour" /> an hour
						</Row>
						<Row>
							<span className="block">
								Merge matches automatically
								<InfoTip>
									Only merges when there's at most one EAN among them, no store lists them as separate items, the prices
									are within the limit below, and none was split out by hand. Identical titles also need three words or
									more, so two games that are both called "Ludo" stay apart. Every automatic merge is listed on{" "}
									<a href="#/manage/grouping" className="text-ink underline">
										Grouping review
									</a>
									, where you can undo it.
								</InfoTip>
							</span>
							<Choice t={TUNABLES.autoMerge} label="Merge automatically" labels={MERGE_LABELS} />
						</Row>
						<Row
							dim={get(TUNABLES.autoMerge) === "off"}
							help="Scalped sets can sell at 5 to 10 times list price, so keep this generous. 0 turns the price check off."
						>
							Only merge when the dearest price is at most
							<Blank t={TUNABLES.autoMergePriceRatio} label="Largest price ratio" /> the cheapest
						</Row>
					</Group>

					<Group
						id="sweeps"
						title="Sweeps and watches"
						intro="Sweeps read whole catalogues; watches check single products you picked, much more often."
					>
						<Row help="A store's own cadence, set under Stores, wins over this.">
							Sweep each store every
							<Blank t={TUNABLES.sweepEvery} label="Default sweep interval in minutes" />
						</Row>
						<Row help="About 100 products a page. A cut-off sweep shows ⚠ under Stores; raise this, or pick categories for that store.">
							Stop a sweep after
							<Blank t={TUNABLES.sweepMaxPages} label="Page limit per sweep" />
						</Row>
						<Row help="Longer is gentler on the stores, shorter finishes sooner.">
							Wait
							<Blank t={TUNABLES.sweepPagePause} label="Pause between pages in milliseconds" /> between pages
						</Row>
						<Row help="Give or take a quarter, so checks don't line up. Applies to existing watches from their next check.">
							Check hot watches every
							<Blank t={TUNABLES.watchHot} label="Hot watch interval in seconds" />, normal ones every
							<Blank t={TUNABLES.watchNormal} label="Normal watch interval in minutes" /> and cold ones every
							<Blank t={TUNABLES.watchCold} label="Cold watch interval in minutes" />
						</Row>
					</Group>

					<Group
						id="buying"
						title="Buying"
						intro="Buy now fills in the cart and checkout at Pokechest and Ringo, then waits for you to approve the payment in Vipps or Klarna."
					>
						<Row help="After this the purchase is marked failed. Nothing is paid without your approval.">
							Wait
							<Blank t={TUNABLES.approvalWait} label="Approval wait in minutes" /> for you to approve a payment
						</Row>
						<CustomerRow />
					</Group>

					<Group id="housekeeping" title="Housekeeping">
						<Row help="Price and stock history is kept forever; this only clears old feed entries and files.">
							Keep the feed and alert log for
							<Blank t={TUNABLES.keepEvents} label="Days to keep the feed" /> and checkout screenshots for
							<Blank t={TUNABLES.keepScreenshots} label="Days to keep screenshots" />
						</Row>
					</Group>
				</div>
			</div>
		</Page>
	);
}

function StatusWord({ status, envName }: { status: StatusRes | null; envName: string }) {
	if (!status) return null;
	return (
		<span className="text-xs text-muted">
			{status.configured ? <span className="font-medium text-ink">Connected</span> : "Not set"}
			{status.fromEnv && ` · from ${envName}`}
		</span>
	);
}

function FailedLine({ error }: { error: string }) {
	return (
		<span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
			<XCircle className="size-3.5 shrink-0 text-muted" aria-hidden />
			<StateLabel state="fault" label="Failed" />
			<span className="min-w-0 break-words text-muted">{error}</span>
		</span>
	);
}

function DiscordRow() {
	const [status, setStatus] = useState<StatusRes | null>(null);
	const [webhook, setWebhook] = useState("");
	const [result, setResult] = useState<ReactNode>(null);
	const [saving, setSaving] = useState(false);

	const refresh = () =>
		fetch("/api/settings/discord_webhook_url")
			.then((r) => r.json())
			.then(setStatus);

	useEffect(() => {
		void refresh();
	}, []);

	const save = async () => {
		setSaving(true);
		try {
			await fetch("/api/settings", {
				method: "PATCH",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ "discord.webhook_url": webhook.trim() }),
			});
			setWebhook("");
			setResult(<span className="text-muted">Saved</span>);
			await refresh();
		} finally {
			setSaving(false);
		}
	};

	const test = async () => {
		setResult(<span className="text-muted">Sending…</span>);
		const res = await fetch("/api/settings/test-discord", { method: "POST" });
		const data = (await res.json()) as { ok: true } | { ok: false; error: string };
		setResult(
			data.ok ? (
				<span className="inline-flex items-center gap-1 text-ink">
					<CheckCircle2 className="size-3.5" aria-hidden /> Delivered
				</span>
			) : (
				<FailedLine error={data.error} />
			),
		);
	};

	return (
		<Row>
			<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
				<span>
					Discord webhook
					<InfoTip>From a Discord channel: Edit channel, Integrations, Webhooks, New webhook, Copy URL.</InfoTip>
				</span>
				<StatusWord status={status} envName="DISCORD_WEBHOOK_URL" />
				{result && <span className="text-xs">{result}</span>}
			</div>
			<div className="mt-1 mb-1 flex flex-wrap gap-1.5">
				<input
					type="password"
					aria-label="Discord webhook address"
					placeholder={status?.configured ? "Paste a new address to replace it" : "https://discord.com/api/webhooks/…"}
					value={webhook}
					onChange={(e) => setWebhook(e.target.value)}
					className={`${inputClass} flex-1 basis-64`}
				/>
				<Button variant="primary" onClick={save} disabled={!webhook || saving}>
					{saving ? "Saving…" : "Save"}
				</Button>
				<Button variant="secondary" onClick={test} disabled={!status?.configured}>
					Send test
				</Button>
			</div>
		</Row>
	);
}

function ShopAlertsRow() {
	const [count, setCount] = useState<number | null>(null);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		fetch("/api/settings/store-alerts")
			.then((r) => r.json())
			.then((d: { warehouseCodes: string[] }) => setCount(d.warehouseCodes.length))
			.catch(() => {});
	}, []);

	return (
		<div>
			<Row
				help="When a watched product is back on the shelf in a Nille or Ringo shop you picked. Ringo only checks the shops picked here."
				control={
					<Button variant="secondary" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="gap-1">
						{open ? "Done" : "Choose shops"}
						<ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
					</Button>
				}
			>
				In-store alerts from <span className="font-semibold">{count ?? "…"}</span> {count === 1 ? "shop" : "shops"}
			</Row>
			{open && (
				<div className="border-t border-hairline px-3 py-2.5">
					<StoreAlertPicker embedded onSaved={setCount} />
				</div>
			)}
		</div>
	);
}

type KeyCheck =
	| {
			ok: true;
			label: string | null;
			usage: number | null;
			limit: number | null;
			limitRemaining: number | null;
			isFreeTier: boolean;
	  }
	| { ok: false; error: string };

/** "$3.41 of $10" — or just "$3.41 used" when the key has no cap. */
function describeCredit(check: Extract<KeyCheck, { ok: true }>): string {
	const usage = check.usage ?? 0;
	if (check.limit == null) return `$${usage.toFixed(2)} used, no cap`;
	return `$${usage.toFixed(2)} of $${check.limit.toFixed(2)} used`;
}

/** "$0.10 in / $0.40 out per M" — or the name, when the model is unpriced. */
function priceLabel(m: ModelOption): string {
	if (m.promptPerMillion == null && m.completionPerMillion == null) {
		return m.name ?? m.id;
	}
	const fmt = (n: number | null) => (n == null ? "?" : `$${n.toFixed(2)}`);
	return `${fmt(m.promptPerMillion)} in / ${fmt(m.completionPerMillion)} out per M`;
}

/**
 * The OpenRouter key and model: what AI matching on Grouping review, and the
 * automatic reading below, run on.
 */
function OpenRouterRows() {
	const [status, setStatus] = useState<OpenRouterStatusRes | null>(null);
	const [apiKey, setApiKey] = useState("");
	const [keyResult, setKeyResult] = useState<ReactNode>(null);
	const [saving, setSaving] = useState(false);
	const [models, setModels] = useState<ModelOption[] | null>(null);
	const [modelsError, setModelsError] = useState<string | null>(null);
	const [model, setModel] = useState("");
	const [savingModel, setSavingModel] = useState(false);
	const [probeResult, setProbeResult] = useState<ReactNode>(null);
	const [probing, setProbing] = useState(false);

	const refresh = () =>
		fetch("/api/ai/openrouter")
			.then((r) => r.json())
			.then((d: OpenRouterStatusRes) => {
				setStatus(d);
				setModel(d.model ?? "");
			});

	useEffect(() => {
		void refresh();
		// The catalogue is public, so the picker fills in before a key is saved.
		fetch("/api/ai/openrouter/models")
			.then((r) => r.json())
			.then((d: { ok: boolean; models?: ModelOption[]; error?: string }) => {
				if (d.ok && d.models) setModels(d.models);
				else setModelsError(d.error ?? "could not load models");
			})
			.catch((e: Error) => setModelsError(e.message));
	}, []);

	const saveKey = async () => {
		setSaving(true);
		try {
			await fetch("/api/ai/openrouter", {
				method: "PATCH",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ apiKey: apiKey.trim() }),
			});
			setApiKey("");
			setKeyResult(<span className="text-muted">Saved</span>);
			await refresh();
		} finally {
			setSaving(false);
		}
	};

	const testKey = async () => {
		setKeyResult(<span className="text-muted">Checking…</span>);
		const res = await fetch("/api/ai/openrouter/test", { method: "POST" });
		const data = (await res.json()) as KeyCheck;
		setKeyResult(
			data.ok ? (
				<span className="inline-flex flex-wrap items-center gap-1.5 text-ink">
					<CheckCircle2 className="size-3.5" aria-hidden /> Reachable
					<span className="text-muted">
						{describeCredit(data)}
						{data.isFreeTier && ", free tier"}
					</span>
				</span>
			) : (
				<FailedLine error={data.error} />
			),
		);
	};

	const saveModel = async () => {
		setSavingModel(true);
		try {
			await fetch("/api/ai/openrouter", {
				method: "PATCH",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ model: model.trim() }),
			});
			await refresh();
		} finally {
			setSavingModel(false);
		}
	};

	/**
	 * One chunk of real pending listings through the chosen model, reported
	 * in full: how long it took says whether a chunk finishes inside the
	 * deadline, and what it said says whether the answer's shape is the
	 * problem. Nothing is saved.
	 */
	const probe = async () => {
		setProbing(true);
		setProbeResult(<span className="mt-2 block text-xs text-muted">Reading a chunk…</span>);
		try {
			const res = await fetch("/api/ai/openrouter/probe", { method: "POST" });
			const d = (await res.json()) as
				| {
						ok: true;
						elapsedMs: number;
						raw: string;
						results: { title: string; attributeKey: string | null }[];
				  }
				| { ok: false; error: string; status: number | null; elapsedMs: number; raw: string | null };
			setProbeResult(
				<div className="mt-2 rounded-md border border-hairline bg-ground p-2.5 text-xs">
					{d.ok ? (
						<>
							<p className="mb-1.5 text-ink">
								Read {d.results.length} listing{d.results.length === 1 ? "" : "s"} in {(d.elapsedMs / 1000).toFixed(1)}
								s, identified {d.results.filter((r) => r.attributeKey).length}. Nothing was saved.
							</p>
							<ul className="flex flex-col gap-1">
								{d.results.map((r) => (
									<li key={r.title} className="min-w-0">
										<span className="block truncate text-ink">{r.title}</span>
										<span className="block font-mono text-[10px] break-all text-muted">
											{r.attributeKey ?? "not identified"}
										</span>
									</li>
								))}
							</ul>
						</>
					) : (
						<>
							<FailedLine error={d.error} />
							<p className="text-muted">after {(d.elapsedMs / 1000).toFixed(1)}s</p>
							{d.raw && (
								<pre className="mt-1.5 max-h-40 overflow-auto font-mono text-[10px] break-all whitespace-pre-wrap text-muted">
									{d.raw}
								</pre>
							)}
						</>
					)}
				</div>,
			);
		} finally {
			setProbing(false);
		}
	};

	return (
		<>
			<Row>
				<div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
					<span>
						OpenRouter key
						<InfoTip>Only AI matching uses this key. Get one at openrouter.ai under Keys.</InfoTip>
					</span>
					<StatusWord status={status} envName="OPENROUTER_API_KEY" />
					{keyResult && <span className="text-xs">{keyResult}</span>}
				</div>
				<div className="mt-1 mb-1 flex flex-wrap gap-1.5">
					<input
						type="password"
						aria-label="OpenRouter API key"
						placeholder={status?.configured ? "Paste a new key to replace it" : "sk-or-v1-…"}
						value={apiKey}
						onChange={(e) => setApiKey(e.target.value)}
						className={`${inputClass} flex-1 basis-64`}
					/>
					<Button variant="primary" onClick={saveKey} disabled={!apiKey || saving}>
						{saving ? "Saving…" : "Save"}
					</Button>
					<Button variant="secondary" onClick={testKey} disabled={!status?.configured}>
						Test key
					</Button>
				</div>
			</Row>
			<Row>
				<span className="block">
					Model
					<InfoTip>
						{status?.modelFromEnv
							? "Set by OPENROUTER_MODEL on the server; change it there."
							: modelsError
								? `The model list didn't load (${modelsError}); type a model id instead.`
								: "No default: cheap models change too often for a built-in choice to stay good. Prices show as you type."}
					</InfoTip>
				</span>
				<div className="mt-1 mb-1 flex flex-wrap gap-1.5">
					<input
						list="openrouter-models"
						aria-label="OpenRouter model"
						placeholder="Type to filter, e.g. flash"
						value={model}
						onChange={(e) => setModel(e.target.value)}
						disabled={status?.modelFromEnv}
						className={`${inputClass} flex-1 basis-64 font-mono placeholder:font-sans disabled:opacity-50`}
					/>
					<datalist id="openrouter-models">
						{(models ?? []).map((m) => (
							<option key={m.id} value={m.id} label={priceLabel(m)} />
						))}
					</datalist>
					<Button
						variant="secondary"
						onClick={saveModel}
						disabled={savingModel || status?.modelFromEnv || model.trim() === (status?.model ?? "")}
					>
						{savingModel ? "Saving…" : "Save model"}
					</Button>
					<Button variant="secondary" onClick={probe} disabled={!status?.configured || !status?.model || probing}>
						{probing ? "Reading…" : "Test on real listings"}
					</Button>
				</div>
				{probeResult}
			</Row>
		</>
	);
}

const CUSTOMER_FIELDS: Array<{
	key: keyof CustomerForm;
	label: string;
	type?: string;
	wide?: boolean;
	optional?: boolean;
}> = [
	{ key: "firstName", label: "First name" },
	{ key: "lastName", label: "Last name" },
	{ key: "email", label: "Email", type: "email" },
	{ key: "phone", label: "Phone", type: "tel" },
	{ key: "address1", label: "Address", wide: true },
	{ key: "address2", label: "Address line 2", wide: true, optional: true },
	{ key: "postal", label: "Postal code" },
	{ key: "city", label: "City" },
];

/** Checkout details, folded to a one-line summary until you edit them. */
function CustomerRow() {
	const [form, setForm] = useState<CustomerForm>(EMPTY_CUSTOMER);
	const [open, setOpen] = useState(false);
	const [saved, setSaved] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		fetch("/api/customer")
			.then((r) => r.json())
			.then((d: { customer: Partial<CustomerForm> }) => {
				setForm({ ...EMPTY_CUSTOMER, ...d.customer });
			});
	}, []);

	const missing = CUSTOMER_FIELDS.filter((f) => !f.optional && !form[f.key].trim());

	const save = async () => {
		setSaving(true);
		try {
			await fetch("/api/customer", {
				method: "PATCH",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(form),
			});
			setSaved(true);
		} finally {
			setSaving(false);
		}
	};

	const summary =
		missing.length === 0
			? `${form.firstName} ${form.lastName}, ${form.postal} ${form.city}`
			: `missing ${missing.map((f) => f.label.toLowerCase()).join(", ")}`;

	return (
		<div>
			<Row
				help={missing.length === 0 ? "Filled in at checkout." : "Buy now fails until these are filled in."}
				control={
					<Button variant="secondary" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="gap-1">
						{open ? "Close" : "Edit"}
						<ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
					</Button>
				}
			>
				Checkout details: <span className={missing.length === 0 ? "text-ink" : "text-fault"}>{summary}</span>
			</Row>
			{open && (
				<div className="border-t border-hairline px-3 py-2.5">
					<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
						{CUSTOMER_FIELDS.map((f) => (
							<label key={f.key} className={`flex flex-col gap-1 text-xs text-muted ${f.wide ? "sm:col-span-2" : ""}`}>
								<span>
									{f.label}
									{f.optional && " (optional)"}
								</span>
								<input
									type={f.type ?? "text"}
									value={form[f.key]}
									onChange={(e) => {
										setForm((prev) => ({ ...prev, [f.key]: e.target.value }));
										setSaved(false);
									}}
									className={inputClass}
								/>
							</label>
						))}
					</div>
					<div className="mt-2.5 flex items-center gap-2.5">
						<Button variant="primary" onClick={save} disabled={saving}>
							{saving ? "Saving…" : "Save details"}
						</Button>
						{saved && <span className="text-xs text-muted">Saved</span>}
					</div>
				</div>
			)}
		</div>
	);
}
